import { investorApi } from '@core/api/httpClient'
import { BANK_ACCOUNT_ENDPOINTS } from './bankAccountEndpointPaths'
import {
  parseBankAccountList,
  parseBankAccountDetail,
  parseCreateBankAccountResult,
} from './bankAccountParser'
import type {
  CreateBankAccountPayload,
  UpdateBankAccountPayload,
  UploadStatementPayload,
} from './bankAccountContracts'
import type { BankAccount, BankAccountDetail, CreatedBankAccount } from '../types/bankAccountTypes'

export async function getBankAccounts(): Promise<BankAccount[]> {
  const { data } = await investorApi.get<unknown>(BANK_ACCOUNT_ENDPOINTS.LIST)
  return parseBankAccountList(data)
}

export async function lookupBankAccountById(id: string): Promise<BankAccountDetail> {
  const { data } = await investorApi.get<unknown>(BANK_ACCOUNT_ENDPOINTS.LOOKUP, {
    params: { id },
  })
  return parseBankAccountDetail(data)
}

// Primary-key fetch for the investor's own bank account. Used by the
// details + edit drawer flows where the list row gives us `id` but no
// unmasked IBAN, so the LOOKUP path can't help us.
export async function getBankAccountById(id: string): Promise<BankAccountDetail> {
  const url = BANK_ACCOUNT_ENDPOINTS.DETAIL.replace('{id}', id)
  const { data } = await investorApi.get<unknown>(url)
  return parseBankAccountDetail(data)
}

export async function createBankAccount(
  payload: CreateBankAccountPayload,
): Promise<CreatedBankAccount> {
  const { data } = await investorApi.post<unknown>(BANK_ACCOUNT_ENDPOINTS.CREATE, payload)
  return parseCreateBankAccountResult(data)
}

export async function updateBankAccount(
  payload: UpdateBankAccountPayload,
): Promise<CreatedBankAccount> {
  const { data } = await investorApi.put<unknown>(BANK_ACCOUNT_ENDPOINTS.UPDATE, payload)
  return parseCreateBankAccountResult(data)
}

export async function uploadBankStatement(payload: UploadStatementPayload): Promise<void> {
  const formData = new FormData()
  formData.append('file', payload.file)
  formData.append('id', payload.id)
  await investorApi.post<unknown>(BANK_ACCOUNT_ENDPOINTS.UPLOAD_STATEMENT, formData)
}

export async function setBankAccountAsPrimary(id: string): Promise<void> {
  const url = BANK_ACCOUNT_ENDPOINTS.SET_PRIMARY.replace('{id}', id)
  await investorApi.patch<unknown>(url)
}
